import React from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBriefcase,
  faChartLine,
  faChartBar,
  faCalculator,
  faChartPie,
  faFileInvoiceDollar,
  faFileSignature,
  faFileAlt,
  faEnvelope,
  faCopy,
  faFolderOpen,
  faStickyNote,
  faCalendarAlt,
  faFileExport,
  faFileImport,
  faPercentage,
  faPercent,
  faBan,
  faSlidersH,
  faExchangeAlt,
  faHistory,
  faUser,
  faTasks,
  faMoneyBillWave,
  faUsers,
  faUserTie,
  faAddressCard,
  faUniversity,
  faClipboardList,
  faUserCircle,
  faChevronRight,
  faClipboardCheck,
  faShieldAlt,
  faLayerGroup,
  faFileContract,
  faUserSecret,
  faBuilding,
  faCreditCard,
  faThList,
  faUserCheck,
  faStream,
  faThLarge,
  faMoneyCheckAlt,
  faHandHoldingUsd,
  faSignature,
  faCoins,
  faEdit,
  faUndoAlt,
  faSearchDollar,
  faCode,
  faArrowLeft,
  faHardHat,
  faExclamationTriangle,
  faRuler,
  faWeightHanging,
  faClipboard,
  faChartArea,
  faTachometerAlt,
  faFunnelDollar,
  faLandmark,
  faIdCard,
  faReceipt,
  faBook,
  faHourglassHalf,
  faSignInAlt,
  faBalanceScale,
  faScroll,
  faGavel,
  faTable,
  faCubes,
  faCashRegister,
  faCalendarCheck,
  faFileInvoice,
  faListOl,
  faPiggyBank,
  faHandshake,
  faArrowCircleDown,
} from "@fortawesome/free-solid-svg-icons";

const ICON_GROUPS = [
  [faMoneyCheckAlt, ["Loan Overview"]],
  [faChartLine, ["Sales Tracker", "LA Sales Tracker"]],
  [faCalculator, ["Cashflow Model"]],
  [faChartPie, ["Cashflow Output"]],
  [faFileInvoiceDollar, ["Completion Statement"]],
  [faFileSignature, ["Loan Model"]],
  [faFileAlt, ["LA Loan Model"]],
  [faChartBar, ["LA  Cashflow Model"]],
  [faFileContract, ["Letters"]],
  [faCopy, ["Master Templates", "Templates"]],
  [faFolderOpen, ["Loan Documents"]],
  [faStickyNote, ["Notes", "Key Notes"]],
  [faCalendarAlt, ["Monthly Statements"]],
  [faFileExport, ["Outbound Payments"]],
  [faFileImport, ["Inbound Payments"]],
  [faPercentage, ["Fees", "Adhoc Fees (Due)"]],
  [faBan, ["Loan Termination"]],
  [faSlidersH, ["Loan Adjustment"]],
  [faExchangeAlt, ["CARS Integration", "CARS"]],
  [faHistory, ["Change Logs", "User Activity Logs"]],
  [faUser, ["Profile"]],
  [faTasks, ["Tasks View", "Standard Tasks", "Wishlist"]],
  [faBriefcase, ["Portfolio Dashboard"]],
  [faMoneyBillWave, ["Inbound Payment Allocation", "Payment Allocation (IN)"]],
  [faLayerGroup, ["Exposure Groups"]],
  [faUserTie, ["Borrower Profiles", "User Groups Management", "Borrower  Details"]],
  [faAddressCard, ["Application Users", "Professional Contacts"]],
  [faUniversity, ["Third Party Accounts"]],
  [faClipboardList, ["Audit Logs", "User Audit Logs"]],
  [faHandHoldingUsd, ["Borrower Overview"]],
  [faUsers, ["Account Management"]],
  [faUserCircle, ["User Management"]],
  [faEnvelope, ["Redemption Letter"]],
  [faClipboardCheck, ["Annual Review"]],
  [faShieldAlt, ["Risk Rating"]],
  [faUserSecret, ["Data Redaction Queue"]],
  [faBuilding, ["Company House Overview", "Companies House Overview"]],
  [faCreditCard, ["Payments", "Payments & Surplus"]],
  [faThList, ["All Tasks"]],
  [faUserCheck, ["Director Approval Screen"]],
  [faStream, ["Loan Tranches"]],
  [faThLarge, ["Overview"]],
  [faSignature, ["Signed Offer letter"]],
  [faCoins, ["DD & Surplus", "Direct Debit"]],
  [faEdit, ["Amendment Overview"]],
  [faUndoAlt, ["Reversal"]],
  [faSearchDollar, ["Experian"]],
  [faCode, ["Integration Logs"]],
  [faHardHat, ["Site Visits", "Interim Report"]],
  [faPercent, ["SVR"]],
  [faExclamationTriangle, ["Arrears Watchlist"]],
  [faUserTie, ["Professional Details"]],
  [faRuler, ["Pricing Perimeter"]],
  [faWeightHanging, ["Provision"]],

  // Portfolio
  [faClipboard, ["Portfolio Summary"]],
  [faChartArea, ["Portfolio Charts"]],
  [faTachometerAlt, ["Operational Efficiency"]],
  [faFunnelDollar, ["Fund-Level Report"]],
  [faLandmark, ["Treasury Reporting"]],

  // Borrower summary
  [faIdCard, ["Borrower Details"]],
  [faReceipt, ["Borrower Account Statements"]],

  // Logs
  [faBook, ["Borrower Logs"]],
  [faHourglassHalf, ["Unprocessed Loans"]],
  [faSignInAlt, ["System Login Audit"]],
  [faBalanceScale, ["GL Audit Trail"]],

  // Loan administration
  [faScroll, ["Credit Documents"]],
  [faGavel, ["Covenant Compliance"]],
  [faTable, ["Reporting Tracker"]],
  [faCubes, ["Pool Balance"]],
  [faCashRegister, ["Payments Report"]],
  [faCalendarCheck, ["Monthly Updates"]],

  // Loan management
  [faFileInvoice, ["Invoices"]],
  [faPercentage, ["Interest Rates"]],
  [faFileInvoiceDollar, ["Interest Statement"]],
  [faChartBar, ["Loan Metrics"]],
  [faListOl, ["Loan Tranche Schedule"]],
  [faPiggyBank, ["Principal Repayments"]],
  [faHandshake, ["Drawdown Request"]],
  [faArrowCircleDown, ["Drawdowns"]],
];

const normalizeName = (name) =>
  typeof name === "string" ? name.trim().toLowerCase() : "";

const ICON_MAP = Object.fromEntries(
  ICON_GROUPS.flatMap(([icon, names]) =>
    names.map((name) => [normalizeName(name), icon])
  )
);

const getMenuIcon = (name) => ICON_MAP[normalizeName(name)];

const startsWithUniqueChar = (name) => {
  if (!name) return false;
  const firstChar = name.trim().charAt(0);
  return !/[a-zA-Z0-9]/.test(firstChar);
};

const BACK_ARROW = "←";
export const startsWithBackArrow = (name) =>
  typeof name === "string" && name.trim().startsWith(BACK_ARROW);

export const stripMenuIconPrefix = (name) =>
  startsWithBackArrow(name) ? name.replace(BACK_ARROW, "").trim() : name;

export const hasMenuIcon = (name) => {
  if (startsWithBackArrow(name)) return true;
  if (getMenuIcon(name)) return true;
  if (startsWithUniqueChar(name)) return false;
  return true; // Use default for others
};

const M_MenuIcon = ({ name, compact = false, ...props }) => {
  if (startsWithBackArrow(name)) {
    return (
      <FontAwesomeIcon
        icon={faArrowLeft}
        style={{ fontSize: "1rem", ...props.style }}
        {...props}
      />
    );
  }

  const icon = getMenuIcon(name);

  if (icon) {
    return (
      <FontAwesomeIcon
        icon={icon}
        style={{ fontSize: "1rem", ...props.style }}
        {...props}
      />
    );
  }

  if (startsWithUniqueChar(name)) {
    // In rail/compact mode, fall back to the first character of the title
    // so these items still show *something* instead of disappearing.
    if (compact) {
      const firstChar = (name || "").trim().charAt(0) || "?";
      return (
        <span
          aria-hidden="true"
          style={{
            fontSize: "1rem",
            fontWeight: 700,
            lineHeight: 1,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            ...props.style,
          }}
        >
          {firstChar}
        </span>
      );
    }
    return null;
  }

  return (
    <FontAwesomeIcon
      icon={faChevronRight}
      style={{
        fontSize: "0.625rem",
        opacity: 0.4,
        marginLeft: "2px",
        ...props.style,
      }}
      {...props}
    />
  );
};

M_MenuIcon.propTypes = {
  name: PropTypes.string,
  style: PropTypes.object,
  compact: PropTypes.bool,
};

export default React.memo(M_MenuIcon);
